import { Store } from "pullstate";
import {
  ScormExportFileDeliveryStatus,
  SettingInheritance,
  SkuApprovalStatus,
} from "services/KnowfullyAdminClient";

export type PersistanceStore = {
  questionFilterSearchTerm: string;
  questionFilterTypeId?: string;

  userFilterSearchTerm: string;
  userFilterApplicationId?: string;

  userSkusFilterSearchTerm: string;
  userSkusFilterStartDate?: Date;
  userSkusFilterChargebackRenewalDate?: Date;
  userSkusFilterEndDate?: Date;

  userSkusFilterSkuFormatIds: string[];
  userSkusFilterStateIds: string[];

  productFilterSearchTerm: string;
  productFilterTypeId?: string;

  agreementFilterSearchTerm: string;
  agreementFilterStatusIds: string[];
  hasAgreementStatusInitialized: boolean;

  evaluationFilterSearchTerm: string;
  evaluationFilterTypeId?: string;
  evaluationFilterIsActive?: boolean;

  catalogContentSearchTerm: string;
  catalogContentCorporateEntityId?: string;
  catalogContentMinPrice?: number;
  catalogContentMaxPrice?: number;

  skuContentSearchTerm: string;
  skuContentOwnerOrganizationId?: string;
  skuContentMinPrice?: number;
  skuContentMaxPrice?: number;

  skuFilterSearchTerm: string;

  skuRegistrationsSearchTerm: string;

  skuAttendanceSearchTerm: string;

  skuMinPrice?: number;
  skuMaxPrice?: number;
  skuCreditMin?: number;
  skuCreditMax?: number;
  skuEventStartDate?: Date;
  skuEventEndDate?: Date;
  skuReleaseDateFrom?: Date;
  skuReleaseDateTo?: Date;
  skuOfflineDateFrom?: Date;
  skuOfflineDateTo?: Date;
  skuCreditSubmissionExpirationDateFrom?: Date;
  skuCreditSubmissionExpirationDateTo?: Date;
  skuExpirationDateFrom?: Date;
  skuExpirationDateTo?: Date;
  productIds?: string[];
  productVersionIds?: string[];
  skuStatusIds?: string[];
  skuFormatIds?: string[];
  skuAvailabilityIds?: string[];
  skuFilterQualificationIds?: string[];
  skuQualificationProfessionIds?: string[];
  skuTagIds?: string[];
  skuApprovalStatus?: SkuApprovalStatus;
  skuFormatFilterSearchTerm: string;
  regexValidationSearchTerm: string;
  regexValidationFilterLocationIds: string[];
  regexValidationFilterProfessionIds: string[];
  regexValidationFilterCredentialIds: string[];
  productVersionFilterSearchTerm: string;

  resourceLibraryItemFilterSearchTerm?: string;
  resourceLibraryItemFilterDateFrom?: Date;
  resourceLibraryItemFilterDateTo?: Date;

  certificateSearchTerm: string;
  certificateThemeSearchTerm: string;
  certificateTemplateSearchTerm: string;

  attributeSearchTerm: string;
  contentBlockSearchTerm: string;

  templateSearchTerm: string;

  menuSearchTerm: string;
  menuItemSearchTerm: string;

  qualificationSearchTerm: string;

  licenseSearchTerm: string;
  licenseFilterDesignationIds: string[];
  licenseFilterProfessionIds: string[];
  licenseFilterLocationIds: string[];
  licenseFilterLicensureBodyIds: string[];

  licensureBodySearchTerm: string;

  submissionAuthoritySearchTerm: string;

  userDesignationChangeLogSearchTerm: string;

  webinarSearchTerm: string;
  webinarStartDateSearch?: Date;
  webinarEndDateSearch?: Date;

  requestSearchTerm: string;
  requestStartDateSearch?: Date;
  requestEndDateSearch?: Date;
  requestStatusId?: string;
  requestTypeId?: string;
  creditSubmissionExpirationDateFrom?: Date;
  creditSubmissionExpirationDateTo?: Date;
  qualificationId?: string;
  professionId?: string;
  stateId?: string;

  settingGroupId?: string;
  settingTypeId?: string;
  settingsInheritance?: SettingInheritance;

  attributeFilterSearchTerm: string;
  seriesFilterSearchTerm: string;
  wizardFilterSearchTerm: string;
  settingsFilterSearchTerm: string;

  resourceFilterSearchTerm: string;
  mediaLibraryFilterSearchTerm: string;
  organizationFilterSearchTerm: string;
  organizationFilterStatusId?: string;
  organizationFilterTypeId?: string;
  jobDataStatusId?: string;
  jobStatusId?: string;
  jobTypeId?: string;
  importSourceTypeId?: string;
  importJobQuickFilter?: string;
  importJobEventStartDate?: Date;
  importJobEventEndDate?: Date;
  emailTemplateSearchTerm: string;

  emailSearchTerm: string;
  emailStartDateSearch?: Date;
  emailEndDateSearch?: Date;
  emailTemplateId?: string;

  webhookStartDate?: Date;
  webhookEndDate?: Date;
  webhookHasSubscriptionId: boolean;
  webhookSearchTerm: string;

  exportedScormFilesTag?: string;
  exportedScormFilesStatus?: ScormExportFileDeliveryStatus;
  exportedScormFilesStartDate?: Date;
  exportedScormFilesEndDate?: Date;

  notificationConfigSearchTerm: string;
  notificationConfigChannelIds: string[];
  notificationConfigStatusIds: string[];
};

const persistanceStore = new Store<PersistanceStore>({
  questionFilterSearchTerm: "",
  userFilterSearchTerm: "",
  userSkusFilterSearchTerm: "",
  userSkusFilterSkuFormatIds: [],
  userSkusFilterStateIds: [],
  productFilterSearchTerm: "",
  evaluationFilterSearchTerm: "",
  catalogContentSearchTerm: "",
  skuFilterSearchTerm: "",
  skuRegistrationsSearchTerm: "",
  skuAttendanceSearchTerm: "",
  skuFormatFilterSearchTerm: "",
  regexValidationSearchTerm: "",
  regexValidationFilterLocationIds: [],
  regexValidationFilterProfessionIds: [],
  regexValidationFilterCredentialIds: [],
  productVersionFilterSearchTerm: "",
  certificateThemeSearchTerm: "",
  certificateTemplateSearchTerm: "",
  certificateSearchTerm: "",
  templateSearchTerm: "",
  qualificationSearchTerm: "",
  licenseSearchTerm: "",
  licenseFilterDesignationIds: [],
  licenseFilterProfessionIds: [],
  licenseFilterLocationIds: [],
  licenseFilterLicensureBodyIds: [],
  licensureBodySearchTerm: "",
  submissionAuthoritySearchTerm: "",
  userDesignationChangeLogSearchTerm: "",
  menuSearchTerm: "",
  menuItemSearchTerm: "",
  webinarSearchTerm: "",
  webinarStartDateSearch: new Date(),
  webinarEndDateSearch: new Date(),
  requestSearchTerm: "",
  attributeFilterSearchTerm: "",
  seriesFilterSearchTerm: "",
  wizardFilterSearchTerm: "",
  resourceFilterSearchTerm: "",
  mediaLibraryFilterSearchTerm: "",
  organizationFilterSearchTerm: "",
  settingsFilterSearchTerm: "",
  creditSubmissionExpirationDateFrom: new Date(),
  emailTemplateSearchTerm: "",
  skuContentSearchTerm: "",
  emailSearchTerm: "",
  agreementFilterSearchTerm: "",
  agreementFilterStatusIds: [],
  hasAgreementStatusInitialized: false,
  attributeSearchTerm: "",
  contentBlockSearchTerm: "",
  webhookStartDate: undefined,
  webhookEndDate: undefined,
  webhookHasSubscriptionId: false,
  webhookSearchTerm: "",
  exportedScormFilesTag: undefined,
  exportedScormFilesStatus: undefined,
  exportedScormFilesStartDate: undefined,
  exportedScormFilesEndDate: undefined,
  productIds: [],
  productVersionIds: [],
  skuStatusIds: [],
  skuFormatIds: [],
  skuAvailabilityIds: [],
  skuFilterQualificationIds: [],
  skuQualificationProfessionIds: [],
  skuTagIds: [],
  skuApprovalStatus: undefined,
  notificationConfigSearchTerm: "",
  notificationConfigChannelIds: [],
  notificationConfigStatusIds: [],
});

export default persistanceStore;
